import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    LOCALE_ID,
    type ResourceRef,
    Signal,
    computed,
    effect,
    inject,
    resource,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { BlockDirective, ElemDirective } from '@rt-tools/core';
import { BlogArticlesApiService, IBlogArticle, blogCardsOf, provideBlogCms } from '@stair-craft/blog/data-access';
import { BlogArticleCardComponent } from '@stair-craft/blog/ui';
import { CardCarouselComponent, ContactFormComponent } from '@stair-craft/common/components';
import { IStairConfig, LastStairConfigService, SITE_ORIGIN } from '@stair-craft/common/util';

import { HomeHeroComponent } from '../home-hero/home-hero.component';
import { HomeTypeCardComponent } from './home-type-card/home-type-card.component';

const HOME_BLOG_SCHEMA_MARKER: string = 'data-sc-home-blog-schema';
const BEM_BLOCK: string = 'sc-home-page';

@Component({
    selector: 'sc-home-page',
    templateUrl: './home-page.component.html',
    styleUrl: './home-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        RouterLink,

        // rt-tools
        BlockDirective,
        ElemDirective,

        // shared-ui-kit
        CardCarouselComponent,
        ContactFormComponent,

        // blog
        BlogArticleCardComponent,

        // home-private
        HomeHeroComponent,
        HomeTypeCardComponent,
    ],
    // Карусель статей читает их клиентом пакета CMS: его провайдеры едут с ленивой главной, а не с
    // приложением.
    providers: provideBlogCms(),
    host: {
        class: BEM_BLOCK,
    },
})
export default class HomePageComponent {
    readonly #lastConfigStore: LastStairConfigService = inject(LastStairConfigService);
    readonly #doc: Document = inject(DOCUMENT);
    readonly #siteOrigin: string = inject(SITE_ORIGIN);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #localeId: string = inject(LOCALE_ID);
    readonly #api: BlogArticlesApiService = inject(BlogArticlesApiService);

    // Опубликованные статьи CMS; главную рисует сервер на запрос, и он ждёт этого чтения.
    // Сервер CMS не ответил — блока статей на главной нет.
    readonly #storedArticles: ResourceRef<readonly IBlogArticle.State[] | undefined> = resource({
        loader: (): Promise<readonly IBlogArticle.State[]> => this.#api.published(this.#localeId),
    });

    readonly #blogTags: ResourceRef<readonly IBlogArticle.Tag[] | undefined> = resource({
        loader: (): Promise<readonly IBlogArticle.Tag[]> => this.#api.tags(),
    });

    protected readonly blogArticles: Signal<readonly IBlogArticle.State[]> = computed(() => this.#storedArticles.value() ?? []);
    protected readonly blogCards: Signal<readonly IBlogArticle.Card[]> = computed(() =>
        blogCardsOf(this.blogArticles(), this.#blogTags.value() ?? [])
    );

    /**
     * Last stair configuration the user actually built in the constructor.
     * `null` until the user has visited the constructor at least once.
     * Passed to `<sc-home-contact-form>` so the studio sees the design when
     * a contact email comes in.
     */
    protected readonly lastStairConfig: Signal<IStairConfig | null> = this.#lastConfigStore.config;

    constructor() {
        effect((): void => {
            const articles: readonly IBlogArticle.State[] = this.blogArticles();
            if (articles.length === 0) {
                this.#removeBlogItemListSchema();
                return;
            }
            this.#injectBlogItemListSchema(articles);
        });
        this.#destroyRef.onDestroy((): void => {
            this.#removeBlogItemListSchema();
        });
    }

    /**
     * Emits an ItemList schema for the home page blog carousel — gives Google
     * a structured view of the article list it should crawl, complete with
     * positions and titles. Helps with sitelinks and crawl prioritization.
     * Runs on the server render, so the script tag lands in the served HTML.
     */
    #injectBlogItemListSchema(articles: readonly IBlogArticle.State[]): void {
        const itemListElement: Record<string, unknown>[] = articles.map(
            (article: IBlogArticle.State, index: number): Record<string, unknown> => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `${this.#siteOrigin}${article.path}`,
                name: article.title,
            })
        );

        const schema: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: $localize`:@@homeBlogSchemaName:Справочник GoldenCalc`,
            itemListOrder: 'https://schema.org/ItemListOrderAscending',
            numberOfItems: articles.length,
            itemListElement,
        };

        let script: HTMLScriptElement | null = this.#doc.querySelector(`script[${HOME_BLOG_SCHEMA_MARKER}]`);
        if (!script) {
            script = this.#doc.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            script.setAttribute(HOME_BLOG_SCHEMA_MARKER, '');
            this.#doc.head.appendChild(script);
        }
        script.textContent = JSON.stringify(schema);
    }

    #removeBlogItemListSchema(): void {
        const script: HTMLScriptElement | null = this.#doc.querySelector(`script[${HOME_BLOG_SCHEMA_MARKER}]`);
        script?.remove();
    }
}
